import { NodeModel, TreeOptions, TreeSource, NodeSource, FolderOptions } from "@omegagrid/tree";
import rp, { BrowserAdapter } from "@reporting/rp-app-host";
import { Thread } from "@reporting/rp-types/etl";
import { IOThreadTypes } from "../types";
import { AccountingRights } from "@reporting/rp-types/core";

function sortThreads(threads: NodeSource[]) {
	threads.sort((a, b) => {
		const chunksA = a.props.path as string[] || [];
		const chunksB = b.props.path as string[] || [];
		for (let i = 0; i < Math.min(chunksA.length, chunksB.length); i++) {
			if (chunksA[i] < chunksB[i]) return -1;
			if (chunksA[i] > chunksB[i]) return 1;
		}

		if ((a.props.dir) && (b.props.dir)) return chunksA.length < chunksB.length ? -1 : 1;
		if ((a.props.dir) && !(b.props.dir)) return -1;
		if (!(a.props.dir) && (b.props.dir)) return 1;

		if (chunksA.length < chunksB.length) return 1;
		if (chunksA.length > chunksB.length) return -1;

		const t1 = (a.props.thread as Thread);;
		const t2 = (b.props.thread as Thread);

		if (t1.ord_num !== t2.ord_num) return t1.ord_num - t2.ord_num;
		return a.v < b.v ? -1 : 1;
	});
}

export class IOBrowserAdapter extends BrowserAdapter {

	async getTree(): Promise<TreeSource> {
		const threadTypes: { k: IOThreadTypes, v: string }[] = [];
		if (rp.userProvider.hasRight(AccountingRights.IOImport)) threadTypes.push({ k: IOThreadTypes.Import, v: 'Import' });
		if (rp.userProvider.hasRight(AccountingRights.IOExport)) threadTypes.push({ k: IOThreadTypes.Export, v: 'Export' });
		if (rp.userProvider.hasRight(AccountingRights.IODelete)) threadTypes.push({ k: IOThreadTypes.Delete, v: 'Delete' });

		const threads = (await rp.ms('etl').get<Thread[]>(`threads`, {
			where: { 'enabled': 1, 'type': threadTypes.map(t => t.k) },
			fields: ['id_thread', 'description', 'type', 'ord_num'],
			order: [['type', 'asc'], ['description', 'asc'], ['ord_num', 'asc']],
			scope: 1
		})).reduce<Record<string, Thread[]>>((acc, item) => {
			acc[item.type] = acc[item.type] || [];
			acc[item.type].push(item);
			return acc;
		}, {});

		const treeItems: Record<string, NodeSource[]> = {};
		for (const type of threadTypes) {
			if (!threads[type.k]) continue;
			let parents: string[] = [];
			treeItems[type.k] = [];
			threads[type.k].forEach(thread => {
				const chunks = thread.description.split('/');
				const filename = chunks.pop();

				for (let i = 0; i < chunks.length; i++) {
					const dir = chunks[i];
					if (parents.length > i && parents[i] === dir) continue;

					treeItems[type.k].push({
						k: type.k + '-' + dir,
						v: dir,
						l: i + 1,
						collapsed: true,
						folder: this.getFolderOptions(dir),
						props: {path: chunks, dir: true}
					});
				}

				const fileConfig = this.getFileConfigByName(filename) ?? BrowserAdapter.fileConfig.get('sql');
				treeItems[type.k].push({
					k: `io-container-${thread.id_thread}`,
					v: filename,
					l: chunks.length + 1,
					icon: fileConfig.icon,
					props: {thread: thread, path: chunks, dir: false}
				});

				parents = chunks;
			});
		}

		return threadTypes.flatMap(type => {
			if (!treeItems[type.k] || treeItems[type.k].length === 0) return [];
			sortThreads(treeItems[type.k]);
			return [{
				k: type.k,
				v: type.v,
				l: 0,
				collapsed: true,
				folder: BrowserAdapter.folderOptions
			}, ...treeItems[type.k]];
		});
	}

	getFolderOptions(dir: string): FolderOptions {
		let statusColor: string = null;
		switch (dir.toLocaleLowerCase()) {
			case 'finance':
				statusColor = '#2C63A1'; break;
			case 'items':
				statusColor = '#9A2CA1'; break;
			case 'aging':
				statusColor = '#2CA155'; break;
			default:
				return BrowserAdapter.folderOptions;
		}
		return {
			openIcon: { icon: ['fas', 'folder-open'], color: '#dcb67a', statusIcon: 'cube', statusColor },
			closedIcon: { icon: ['fas', 'folder'], color: '#dcb67a', statusIcon: 'cube', statusColor },
		};
	}

	getOptions(): Partial<TreeOptions> {
		return {
			selectMode: 'none',
			expanders: false,
			search: true,
		};
	}

	async onAction(key: string, node: NodeModel) {}
}
